import { useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import {
  CancellationReasonFormData,
  CancelReason,
  makeCancellationReasonSchema,
} from "~/services";

interface UseCancellationReasonFormParams {
  reasons: CancelReason[] | undefined;
  defaultValues: Partial<CancellationReasonFormData>;
  isFeeWindowOpen: boolean;
}

/**
 * The cancel reason form shared by the cancel and the edit modals: the parent/sub-reason
 * cascade, the notes field and the fee waiver. Owns the form state and the derived selection
 * so both modals only have to supply their data source and their submit handler.
 */
export const useCancellationReasonForm = ({
  reasons,
  defaultValues,
  isFeeWindowOpen,
}: UseCancellationReasonFormParams) => {
  // Whether a sub-reason is required depends on the fetched reasons, so the schema is rebuilt
  // when they arrive. react-hook-form reads the resolver off props on every render.
  const schema = useMemo(
    () => makeCancellationReasonSchema(reasons),
    [reasons],
  );

  const form = useForm<CancellationReasonFormData>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues,
  });

  const { setValue, watch, trigger, clearErrors } = form;

  const values = watch();

  const selectedParent = reasons?.find(
    (reason) => reason.id.toString() === values.cancelReasonParentId,
  );

  const subReasons = selectedParent?.children ?? [];

  const showFeeWaiver = Boolean(
    selectedParent?.bearsLateFee && isFeeWindowOpen,
  );

  const handleFieldChange = (
    field: keyof CancellationReasonFormData,
    value: string,
  ) => {
    setValue(field, value, { shouldValidate: true });
  };

  // Changing the parent invalidates both the sub-reason and the waiver: the new parent may
  // not charge a fee at all, and sending a waiver the server rejects fails the whole
  // operation rather than just the waiver.

  const handleParentChange = (value: string) => {
    setValue("cancelReasonParentId", value, { shouldValidate: true });
    setValue("cancelReasonChildId", undefined);
    setValue("waiveFee", false);
    clearErrors("cancelReasonChildId");
  };

  return {
    form,
    values,
    // Exposed so a caller can re-decide fee applicability at submit time, which is what
    // CancelAVisitModal does with it. showFeeWaiver cannot answer that question: it folds in
    // the fee window as it was on the last render.
    selectedParent,
    subReasons,
    showFeeWaiver,
    handleFieldChange,
    handleParentChange,
    validateSubReason: () => void trigger("cancelReasonChildId"),
    setWaiveFee: (waiveFee: boolean) => setValue("waiveFee", waiveFee),
  };
};
